import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { Modal, PageLoader, Spinner } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import { Users, Plus, Star, Recycle, AlertTriangle, Check } from "lucide-react";

const STEPS = [
  { title: "Send to the Scrap Yard?", body: "This robot will be handed to the station Scrap Yard. Continue?", cta: "Yes, continue" },
  { title: "It will be reduced to scraps", body: "Once recycled, your robot is permanently dismantled — melted down into parts for new bots. This CANNOT be undone.", cta: "I understand, proceed" },
  { title: "Final confirmation", body: "Last chance. Tick the box to confirm you knowingly recycle this robot forever.", cta: "Recycle forever" },
];

export default function Pets() {
  const navigate = useNavigate();
  const { refreshPet, patchUser } = useOutletContext();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [scrap, setScrap] = useState(null);
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState(false);

  const load = () => api.get("/pets").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <PageLoader label="Fetching roster" />;

  const setActive = async (p) => {
    setBusy(p.pet_id);
    try { await api.post("/pet/switch", { pet_id: p.pet_id }); await refreshPet(); await load();
      toast.success(`${p.name} is now your active unit.`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const next = () => { if (step < 2) setStep((s) => s + 1); else doRelease(); };
  const doRelease = async () => {
    if (!checked) { toast.error("Tick the confirmation box"); return; }
    setBusy(scrap.pet_id);
    try {
      const { data: res } = await api.post("/pet/release", { pet_id: scrap.pet_id });
      patchUser({ credits: res.credits }); playSfx("lose");
      toast.success(`${scrap.name} recycled. +${res.scrap_credits} scrap Credits.`);
      setScrap(null); await refreshPet(); await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const canAdd = data.pets.length < data.max;

  return (
    <div data-testid="pets-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <span className="label-cap text-primary flex items-center gap-2"><Users className="w-4 h-4" /> Fabrication Roster</span>
          <h1 className="text-4xl font-black tracking-tighter mt-2 font-head">My Robots</h1>
          <p className="text-muted-foreground mt-1 mono text-sm">{data.pets.length} / {data.max} units</p>
        </div>
        <button onClick={() => navigate("/app/create")} data-testid="fabricate-more-btn" disabled={!canAdd} className={canAdd ? "btn-primary" : "btn-ghost opacity-60"}>
          <Plus className="w-4 h-4" /> {canAdd ? "Fabricate" : "Roster full"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.pets.map((p) => {
          const active = p.pet_id === data.active_pet_id;
          return (
            <div key={p.pet_id} data-testid={`robot-${p.pet_id}`} className={`panel p-4 flex flex-col items-center ${active ? "panel-glow" : ""}`}>
              <LayeredStack layers={p.layers} core={p.core_color} rig={p.rig} cpu={p.cpu} size={130} state={p.thermal >= 100 || p.battery <= 0 ? "off" : "idle"} />
              <div className="font-head font-bold mt-2">{p.name}</div>
              <div className="label-cap">{p.temperament}</div>
              {active && <span className="mt-1 px-2 py-0.5 rounded text-[9px] font-black bg-primary text-primary-foreground flex items-center gap-1"><Star className="w-3 h-3" /> ACTIVE</span>}
              <div className="flex gap-2 mt-3 w-full">
                {!active && <button onClick={() => setActive(p)} data-testid={`switch-${p.pet_id}`} disabled={busy === p.pet_id} className="btn-primary flex-1 text-xs px-2 py-2">Set active</button>}
                <button onClick={() => { setScrap(p); setStep(0); setChecked(false); }} data-testid={`scrap-${p.pet_id}`} className={`btn-ghost text-xs px-2 py-2 ${active ? "flex-1" : ""}`}><Recycle className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
        {canAdd && (
          <button onClick={() => navigate("/app/create")} className="panel p-4 flex flex-col items-center justify-center min-h-[220px] border-dashed hover:border-primary hover:text-primary text-muted-foreground">
            <Plus className="w-10 h-10 mb-2" /><span className="font-bold text-sm">Fabricate new bot</span>
          </button>
        )}
      </div>

      <Modal open={!!scrap} onClose={() => busy === null && setScrap(null)} testId="scrap-modal">
        {scrap && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-[var(--radius)] bg-accent/15 text-accent grid place-items-center"><AlertTriangle className="w-6 h-6" /></span>
              <div><div className="label-cap text-accent">Scrap Yard · Step {step + 1} of 3</div><h3 className="font-head text-xl font-black">{STEPS[step].title}</h3></div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{STEPS[step].body}</p>
            {step === 2 && (
              <label className="flex items-center gap-3 mb-4 cursor-pointer select-none" data-testid="scrap-confirm-checkbox">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="w-5 h-5 accent-[hsl(var(--accent))]" />
                <span className="text-sm font-semibold">I know {scrap.name} will be recycled forever.</span>
              </label>
            )}
            <div className="flex gap-3">
              <button onClick={() => setScrap(null)} disabled={busy !== null} className="btn-ghost flex-1">Keep it</button>
              <button onClick={next} data-testid="scrap-next-btn" disabled={busy !== null || (step === 2 && !checked)} className="btn-accent flex-1">
                {busy !== null ? <Spinner className="w-4 h-4" /> : STEPS[step].cta}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
